/* A small key-value store over IndexedDB, and an in-memory stand-in with the same shape.
   Everything the app keeps in the browser goes through one of these: the collection cache
   here, and in apps/web the edits and the trainer's setup. Values are structured-cloned, so
   a Map or a Date survives the trip. */

export interface KV {
  get<T>(key: string): Promise<T | undefined>;
  set(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

const DB_NAME = "preflop";
/** Every store the app uses, created together: adding one means bumping DB_VERSION. */
export const STORES = ["collections", "edits", "settings", "outbox"] as const;
export type StoreName = (typeof STORES)[number];
// 2: the outbox of the practice log.
const DB_VERSION = 2;

let opening: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  opening ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      for (const name of STORES)
        if (!req.result.objectStoreNames.contains(name)) req.result.createObjectStore(name);
    };
    req.onsuccess = () => {
      const db = req.result;
      // Another tab, on a newer build, wants to add a store: step aside rather than block it.
      // This tab is the stale one: its next call finds a version it does not know and carries
      // on in memory until it is reloaded, which is better than the new tab doing so.
      db.onversionchange = () => {
        db.close();
        opening = null;
      };
      resolve(db);
    };
    req.onerror = () => reject(req.error ?? new Error("IndexedDB would not open"));
    req.onblocked = () => reject(new Error("IndexedDB is blocked by another tab"));
  });
  // A failed open is not remembered: the next call tries again.
  opening.catch(() => (opening = null));
  return opening;
}

const done = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });

/** One object store of the app's database. */
export function idbStore(name: StoreName): KV {
  const store = async (mode: IDBTransactionMode): Promise<IDBObjectStore> =>
    (await open()).transaction(name, mode).objectStore(name);
  return {
    get: async <T>(key: string) => done<T | undefined>((await store("readonly")).get(key)),
    set: async (key, value) => void (await done((await store("readwrite")).put(value, key))),
    delete: async (key) => void (await done((await store("readwrite")).delete(key))),
    keys: async () => (await done((await store("readonly")).getAllKeys())).map(String),
  };
}

/** The same store held in memory: for tests, and for a browser that refuses IndexedDB. */
export function memoryStore(): KV {
  const held = new Map<string, unknown>();
  return {
    get: <T>(key: string) => Promise.resolve(held.get(key) as T | undefined),
    set: (key, value) => Promise.resolve(void held.set(key, structuredClone(value))),
    delete: (key) => Promise.resolve(void held.delete(key)),
    keys: () => Promise.resolve([...held.keys()]),
  };
}

/** IndexedDB where there is one, memory where there is not (private windows, old WebViews).
    A store that fails mid-session degrades the same way instead of breaking the page. */
export function browserStore(name: StoreName): KV {
  if (typeof indexedDB === "undefined") return memoryStore();
  const real = idbStore(name);
  const spare = memoryStore();
  let broken = false;
  const guard =
    <A extends unknown[], R>(f: (kv: KV, ...a: A) => Promise<R>) =>
    async (...a: A): Promise<R> => {
      if (!broken)
        try {
          return await f(real, ...a);
        } catch {
          broken = true;
        }
      return f(spare, ...a);
    };
  return {
    get: guard(<T>(kv: KV, key: string) => kv.get<T>(key)) as KV["get"],
    set: guard((kv, key: string, value: unknown) => kv.set(key, value)),
    delete: guard((kv, key: string) => kv.delete(key)),
    keys: guard((kv) => kv.keys()),
  };
}
